/**
 * AreasM2Editor — linhas de material (AreaAmbiente) de UM alvo: um móvel
 * (`itemId`) ou os lançamentos legados sem móvel (`itemId = null`).
 *
 * Reformulação UX 2026-07-31: o editor deixou de ser um bloco por ambiente e
 * passou a operar POR MÓVEL. Duas ações de criação:
 *   - "Adicionar por m²"  → m2_informados (ceil de chapas por material, §1.5)
 *   - "Adicionar chapas"  → chapas_informadas (direto, aceita fração, sem ceil)
 * O XOR entre os dois modos é do backend (constraint + serializer); aqui cada
 * linha edita apenas o campo do seu modo.
 *
 * Dados vêm prontos do pai (`linhas` já filtradas por alvo) — o componente não
 * busca nada; após cada mutação chama `onMutate` (pai recarrega + dispara
 * "orc2:preco-invalidado" para o ComposicaoPrecoDivisor recarregar sozinho).
 * Zero scroll lateral: Table do design system (table-layout fixed + ellipsis).
 */
import React, { useState } from "react";
import { FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarAreaAmbiente,
  criarAreaAmbiente,
  removerAreaAmbiente,
} from "../../../services/motorPreco";

import { fmtMoeda, fmtNumero } from "../helpers";
import {
  InlineHint,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../styles";

const MODO_M2 = "M2";
const MODO_CHAPAS = "CHAPAS";

export default function AreasM2Editor({
  ambienteId,
  itemId = null,
  linhas = [],
  produtosChapa = [],
  editavel = true,
  permitirCriar = true,
  titulo = "Materiais (m² e chapas)",
  onMutate,
}) {
  const { request } = useAuth();

  // Edicao inline por linha: {id: "valor digitado"} (m² ou chapas conforme modo).
  const [edits, setEdits] = useState({});
  // Mini-form de criacao: null | {modo: "M2"|"CHAPAS", produto, quantidade}
  const [novo, setNovo] = useState(null);

  const aposMutacao = async () => {
    setEdits({});
    if (onMutate) await onMutate();
  };

  const abrirNovo = (modo) => setNovo({ modo, produto: "", quantidade: "" });

  const adicionarLinha = async () => {
    const quantidade = String(novo.quantidade).replace(",", ".");
    const dados = { ambiente: ambienteId, produto: novo.produto };
    if (novo.modo === MODO_M2) dados.m2_informados = quantidade;
    else dados.chapas_informadas = quantidade;
    if (itemId) dados.item = itemId;
    await criarAreaAmbiente(request, dados);
    setNovo(null);
    await aposMutacao();
  };

  const salvarLinha = async (linha) => {
    const valor = String(edits[linha.id]).replace(",", ".");
    const dados = linha.modo === MODO_CHAPAS
      ? { chapas_informadas: valor }
      : { m2_informados: valor };
    await atualizarAreaAmbiente(request, linha.id, dados);
    await aposMutacao();
  };

  const removerLinha = async (linha) => {
    await removerAreaAmbiente(request, linha.id);
    await aposMutacao();
  };

  const novoValido = novo
    && Boolean(novo.produto)
    && Number(String(novo.quantidade).replace(",", ".")) > 0;

  if (linhas.length === 0 && !(editavel && permitirCriar)) return null;

  return (
    <section>
      {titulo && <SectionTitle>{titulo}</SectionTitle>}

      {linhas.length > 0 && (
        <TableWrapper>
          <Table>
            <colgroup>
              <col />
              <col style={{ width: "22%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "16%" }} />
              {editavel && <col style={{ width: 84 }} />}
            </colgroup>
            <thead>
              <tr>
                <th>Material (chapa)</th>
                <th>Quantidade</th>
                <th>Chapas</th>
                <th>Custo</th>
                {editavel && <th aria-label="Ações" />}
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => {
                const ehChapas = linha.modo === MODO_CHAPAS;
                const valorAtual = ehChapas
                  ? linha.chapas_informadas
                  : linha.m2_informados;
                const m2Util = linha.m2_util !== null && linha.m2_util !== undefined
                  ? Number(linha.m2_util)
                  : null;
                // Gate G9: linha POR M2 sem geometria da chapa nao conta chapas.
                const semGeometria = !ehChapas && m2Util === null;
                const dirty = edits[linha.id] !== undefined
                  && String(edits[linha.id]) !== String(valorAtual);
                return (
                  <tr key={linha.id}>
                    <td title={linha.aviso || `${linha.produto_codigo} — ${linha.produto_nome}`}>
                      {linha.produto_nome}
                      {semGeometria && (
                        <>
                          {" "}
                          <Pill
                            $tone="danger"
                            title={
                              linha.aviso
                              || "Produto sem largura/comprimento de chapa e sem m² por unidade — sem isso não dá para contar chapas."
                            }
                          >
                            sem área utilizável
                          </Pill>{" "}
                          <Link to="/suprimentos/produtos">
                            cadastrar no estoque
                          </Link>
                        </>
                      )}
                    </td>
                    <td>
                      {editavel ? (
                        <>
                          <Input
                            style={{ width: 80, textAlign: "right" }}
                            inputMode="decimal"
                            value={edits[linha.id] ?? String(valorAtual ?? "")}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [linha.id]: e.target.value,
                              }))
                            }
                            aria-label={
                              ehChapas
                                ? `Chapas de ${linha.produto_nome}`
                                : `m² de ${linha.produto_nome}`
                            }
                          />{" "}
                          <InlineHint>{ehChapas ? "chapas" : "m²"}</InlineHint>
                        </>
                      ) : (
                        `${fmtNumero(valorAtual, 2)} ${ehChapas ? "chapas" : "m²"}`
                      )}
                    </td>
                    <td
                      title={
                        ehChapas
                          ? "Quantidade direta de chapas — sem arredondamento."
                          : m2Util
                            ? `${fmtNumero(linha.m2_informados, 2)} ÷ ${fmtNumero(m2Util, 4)} m²/chapa → ${linha.chapas_calculadas} chapas (arredondado para cima, somando as linhas do mesmo material)`
                            : undefined
                      }
                    >
                      {linha.chapas_calculadas ?? "—"}
                    </td>
                    <td>
                      {linha.custo_estimado !== undefined
                      && linha.custo_estimado !== null
                        ? fmtMoeda(linha.custo_estimado)
                        : "—"}
                    </td>
                    {editavel && (
                      <td>
                        {dirty && (
                          <AsyncButton
                            variant="icon"
                            onClick={() => salvarLinha(linha)}
                            successMessage="Linha atualizada."
                            title="Salvar quantidade"
                          >
                            <FiSave size={12} />
                          </AsyncButton>
                        )}{" "}
                        <AsyncButton
                          variant="icon"
                          onClick={() => removerLinha(linha)}
                          successMessage="Linha removida."
                          title="Remover linha"
                        >
                          <FiTrash2 size={12} />
                        </AsyncButton>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      {editavel && permitirCriar && !novo && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <SecondaryButton type="button" onClick={() => abrirNovo(MODO_M2)}>
            <FiPlus size={12} /> Adicionar por m²
          </SecondaryButton>
          <SecondaryButton type="button" onClick={() => abrirNovo(MODO_CHAPAS)}>
            <FiPlus size={12} /> Adicionar chapas
          </SecondaryButton>
        </div>
      )}

      {editavel && permitirCriar && novo && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <Select
            value={novo.produto}
            onChange={(e) =>
              setNovo((p) => ({ ...p, produto: e.target.value }))
            }
            aria-label="Material (chapa)"
          >
            <option value="">Material (chapa)…</option>
            {produtosChapa.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} — {p.nome}
              </option>
            ))}
          </Select>
          <Input
            style={{ width: 110 }}
            placeholder={novo.modo === MODO_M2 ? "m²" : "Qtde de chapas"}
            inputMode="decimal"
            value={novo.quantidade}
            onChange={(e) =>
              setNovo((p) => ({ ...p, quantidade: e.target.value }))
            }
            aria-label={
              novo.modo === MODO_M2 ? "m² informados" : "Quantidade de chapas"
            }
          />
          <AsyncButton
            variant="secondary"
            onClick={adicionarLinha}
            disabled={!novoValido}
            successMessage={
              novo.modo === MODO_M2
                ? "Linha de m² adicionada."
                : "Chapas adicionadas."
            }
          >
            <FiPlus size={12} />{" "}
            {novo.modo === MODO_M2 ? "Adicionar m²" : "Adicionar chapas"}
          </AsyncButton>
          <SecondaryButton type="button" onClick={() => setNovo(null)}>
            <FiX size={12} /> Cancelar
          </SecondaryButton>
          <InlineHint>
            {novo.modo === MODO_M2
              ? "Chapas são arredondadas para cima por material."
              : "Quantidade direta — aceita fração (ex.: 1,5 chapa), sem arredondamento."}
          </InlineHint>
        </div>
      )}
    </section>
  );
}
